<template>
  <AppShell :hero-mode="true">
    <!-- ===== HERO ===== -->
    <section class="relative overflow-hidden">
      <!-- Background layers -->
      <div class="absolute inset-0 bg-hero-glow"></div>
      <div class="absolute inset-0 bg-grid-dark bg-grid [mask-image:radial-gradient(70%_60%_at_50%_0%,black,transparent)]"></div>

      <div class="container-app relative pt-28 sm:pt-36 pb-20 sm:pb-28 text-center">
        <div class="animate-fade-in">
          <span class="chip border-brand/40 text-brand bg-brand/10 mb-6">
            <span class="h-1.5 w-1.5 rounded-full bg-brand animate-glow-pulse"></span>
            We born hackers — by SiberLab ID
          </span>

          <h1 class="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
            Become a <span class="gradient-text">Hacker.</span>
            <br class="hidden sm:block" />
            Master Cybersecurity.
          </h1>

          <p class="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-ink-muted leading-relaxed">
            Hands-on labs, real attack machines, and AI-mentored challenges. Learn offensive and
            defensive security the way the pros do — then climb the leaderboard.
          </p>

          <div class="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/register" class="btn-primary text-base px-6 py-3">
              Start Hacking Free <i class="fas fa-arrow-right text-sm"></i>
            </a>
            <a href="/modules" class="btn-secondary text-white text-base px-6 py-3">
              <i class="fas fa-compass"></i> Browse Modules
            </a>
          </div>

          <!-- Stats -->
          <div class="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            <div v-for="stat in stats" :key="stat.label" class="card p-5">
              <p class="text-2xl sm:text-3xl font-extrabold gradient-text">{{ stat.value }}</p>
              <p class="mt-1 text-xs sm:text-sm text-ink-muted">{{ stat.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FEATURES ===== -->
    <section class="container-app py-16 sm:py-20">
      <div class="text-center max-w-2xl mx-auto mb-12">
        <p class="eyebrow justify-center">Everything you need</p>
        <h2 class="mt-3 text-3xl sm:text-4xl font-extrabold text-white">One platform, full attack surface</h2>
        <p class="mt-3 text-ink-muted">From fundamentals to advanced exploitation — structured, practical, and gamified.</p>
      </div>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <a
          v-for="f in features"
          :key="f.title"
          :href="f.href"
          class="card card-hover p-7 group"
        >
          <div class="h-12 w-12 grid place-items-center rounded-xl bg-brand/10 border border-brand/20 text-brand text-xl mb-5 group-hover:scale-110 transition-transform">
            <i :class="f.icon"></i>
          </div>
          <h3 class="text-lg font-bold text-white mb-2">{{ f.title }}</h3>
          <p class="text-sm text-ink-muted leading-relaxed">{{ f.desc }}</p>
          <span class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
            Explore <i class="fas fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
          </span>
        </a>
      </div>
    </section>

    <!-- ===== FEATURED MODULES ===== -->
    <section class="container-app pb-16 sm:pb-20">
      <div class="flex items-end justify-between mb-8 gap-4">
        <div>
          <p class="eyebrow">Featured</p>
          <h2 class="mt-3 text-3xl sm:text-4xl font-extrabold text-white">Popular modules</h2>
        </div>
        <a href="/modules" class="btn-secondary shrink-0">View all <i class="fas fa-arrow-right text-xs"></i></a>
      </div>

      <div v-if="loadingModules" class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="n in 3" :key="n" class="card p-4 animate-pulse">
          <div class="h-44 rounded-xl bg-surface-light mb-4"></div>
          <div class="h-5 w-2/3 bg-surface-light rounded mb-3"></div>
          <div class="h-4 w-full bg-surface-light rounded mb-2"></div>
          <div class="h-4 w-1/2 bg-surface-light rounded"></div>
        </div>
      </div>

      <div v-else class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <a
          v-for="course in featuredModules"
          :key="course.id"
          :href="`/modules/${course.id}`"
          class="card card-hover overflow-hidden group flex flex-col"
        >
          <div class="relative h-44 overflow-hidden bg-surface-light">
            <img
              :src="course.imageUrl"
              :alt="course.title"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              @error="onImgError"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent"></div>
            <span
              v-if="course.isExclusive"
              class="badge absolute top-3 right-3 bg-yellow-500 text-black"
            >
              <i class="fas fa-crown"></i> Premium
            </span>
          </div>
          <div class="p-5 flex-1 flex flex-col">
            <h3 class="text-lg font-bold text-white mb-1.5 line-clamp-1">{{ course.title }}</h3>
            <p class="text-sm text-ink-muted line-clamp-2 mb-4 flex-1">{{ course.description }}</p>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="(tag, i) in (course.tags || []).slice(0, 3)" :key="i" class="chip">{{ tag }}</span>
            </div>
          </div>
        </a>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="container-app pb-20">
      <div class="relative overflow-hidden rounded-3xl border border-brand/30 bg-gradient-to-br from-surface to-base-800 p-10 sm:p-16 text-center">
        <div class="absolute inset-0 bg-hero-glow opacity-70"></div>
        <div class="relative">
          <h2 class="text-3xl sm:text-4xl font-extrabold text-white">Ready to break things (legally)?</h2>
          <p class="mt-3 text-ink-muted max-w-xl mx-auto">
            Create a free account and start your first lab in minutes. No credit card required.
          </p>
          <div class="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <a href="/register" class="btn-primary text-base px-7 py-3">Create Free Account</a>
            <a href="/subscribe" class="btn-secondary text-white text-base px-7 py-3">
              <i class="fas fa-crown text-yellow-400"></i> View Pricing
            </a>
          </div>
        </div>
      </div>
    </section>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";

export default {
  name: "LandingPage",
  components: { AppShell },
  data() {
    return {
      loadingModules: true,
      modules: [],
      counts: { modules: null, machines: null, aiLabs: null, hackers: null },
      features: [
        { title: "Course Modules", desc: "Structured learning paths covering web, network, crypto, and more — from beginner to expert.", icon: "fas fa-book", href: "/modules" },
        { title: "Machine Labs", desc: "Boot real vulnerable machines over VPN and own them. Just like the real engagements.", icon: "fas fa-server", href: "/machine-labs" },
        { title: "AI Mentored Lab", desc: "Stuck on a challenge? Get contextual hints and guidance from an AI mentor, 24/7.", icon: "fas fa-robot", href: "/ai-labs" },
        { title: "Competitive Ranking", desc: "Earn points, climb the ranks from Kiddie to Mastermind, and top the global leaderboard.", icon: "fas fa-trophy", href: "/leaderboard" },
        { title: "Real-world Skills", desc: "Practice the exact tools and techniques used by professional penetration testers.", icon: "fas fa-shield-halved", href: "/modules" },
        { title: "Premium Content", desc: "Unlock exclusive subscriber-only modules and labs to accelerate your mastery.", icon: "fas fa-crown", href: "/subscribe" },
      ],
    };
  },
  computed: {
    featuredModules() {
      return this.modules.slice(0, 6);
    },
    stats() {
      const fmt = (n) => (n === null ? "—" : `${n}${n >= 10 ? "+" : ""}`);
      return [
        { value: fmt(this.counts.modules), label: "Hands-on Modules" },
        { value: fmt(this.counts.machines), label: "Attack Machines" },
        { value: fmt(this.counts.aiLabs), label: "AI Mentored Labs" },
        { value: this.counts.hackers === null ? "—" : `${this.counts.hackers}`, label: "Hackers Ranked" },
      ];
    },
  },
  methods: {
    async fetchModules() {
      try {
        const response = await apiClient.get("/modules");
        const baseUrl = process.env.VUE_APP_BASE_URL;
        this.modules = response.data.map((m) => ({
          ...m,
          imageUrl: `${baseUrl}/static/modules/banners/${m.id}`,
        }));
        this.counts.modules = response.data.length;
      } catch (error) {
        console.error("Error fetching modules:", error);
        this.modules = [
          { id: "1", title: "Introduction to Cybersecurity", description: "Learn the fundamentals of ethical hacking and security concepts.", tags: ["Beginner", "Fundamentals"], isExclusive: false, imageUrl: "" },
          { id: "2", title: "Web Application Security", description: "Master OWASP Top 10 vulnerabilities and exploitation techniques.", tags: ["Intermediate", "Web", "OWASP"], isExclusive: true, imageUrl: "" },
          { id: "3", title: "Network Penetration Testing", description: "Scan, enumerate and exploit network infrastructure end to end.", tags: ["Advanced", "Network"], isExclusive: false, imageUrl: "" },
        ];
      } finally {
        this.loadingModules = false;
      }
    },
    async fetchCounts() {
      // Real-data stats — fetched independently so one failure doesn't break the rest
      const [machines, aiLabs, scoreboard] = await Promise.allSettled([
        apiClient.get("/machine-labs"),
        apiClient.get("/ai-mentored-lab"),
        apiClient.get("/scoreboard"),
      ]);
      if (machines.status === "fulfilled") this.counts.machines = machines.value.data.length;
      if (aiLabs.status === "fulfilled") this.counts.aiLabs = aiLabs.value.data.length;
      if (scoreboard.status === "fulfilled") {
        this.counts.hackers = (scoreboard.value.data.scoreboard || []).length;
      }
    },
    onImgError(e) {
      e.target.src = require("@/assets/logo.png");
    },
  },
  mounted() {
    this.fetchModules();
    this.fetchCounts();
  },
};
</script>
